import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCharacterBattleResult } from "../services/OpenAiService";
import { TEXT_MODERATION_BLOCK_CODES } from "../config/moderationPresentation";
import { resolveRulesPath } from "../utils/resolveRulesPath";

const LOADING_STAGES = [
  "Analyzing fighters…",
  "Calculating outcome…",
  "This is taking a moment, hold tight…",
];

// Owns the battle-request lifecycle for the input form: loading/stage-cycling
// state, the API call, and navigating to the result page on success.
// `retryStanding` (useAccess()'s `retry`) is called after a moderation block
// — the block itself can escalate the account's standing server-side as a
// side effect of the same request, so the caller's cached standing needs
// refreshing. Computing the resulting nudge/addendum is the caller's job
// (App.js has the live `standing` value) — this hook just exposes the raw
// `errorCode` so the caller knows when a block occurred.
export function useBattleRequest(retryStanding) {
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [error, setError] = useState(null);
  const [errorAction, setErrorAction] = useState(null); // { label, path } — optional link alongside the error banner
  const [errorCode, setErrorCode] = useState(null);
  const [enforcementPresentation, setEnforcementPresentation] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) { setLoadingStage(0); return; }
    const interval = setInterval(() => {
      setLoadingStage((s) => Math.min(s + 1, LOADING_STAGES.length - 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [loading]);

  async function submit(char1, char2) {
    const c1 = char1.trim();
    const c2 = char2.trim();
    if (!c1 || !c2) {
      setError("Please enter both characters.");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorAction(null);
    setErrorCode(null);
    setEnforcementPresentation(null);

    try {
      const battle = await getCharacterBattleResult(c1, c2);
      navigate(`/battle/${battle.id}`, {
        state: {
          ...battle,
          character1: { ...battle.character1 },
          character2: { ...battle.character2 },
        },
      });
    } catch (err) {
      if (err?.errorCode === "auth_required_for_battle") {
        // Preserve the matchup so it can be restored after sign-in, and open
        // the sign-in modal instead of a dead-end error banner.
        sessionStorage.setItem("pendingBattle", JSON.stringify({ char1: c1, char2: c2 }));
        window.dispatchEvent(new CustomEvent("auth:open", { detail: { reason: "battle" } }));
      } else if (err?.errorCode === "policy_acceptance_required") {
        // Already authenticated here (unlike the auth case above) — no
        // sessionStorage/navigation needed, just retry the exact same
        // submit once the user accepts.
        window.dispatchEvent(new CustomEvent("policy:update-required", {
          detail: { requiredPolicies: err.requiredPolicies, onAccepted: () => submit(c1, c2) },
        }));
      } else {
        setError(err?.message || "Something went wrong. Please try again.");
        setErrorCode(err?.errorCode ?? null);
        setEnforcementPresentation(err?.enforcementPresentation ?? null);
        // account_restricted points at Account Standing; a moderation block
        // points at the specific Rules section it violated, if known.
        if (err?.errorCode === "account_restricted") {
          setErrorAction({ label: "View Account Standing", path: "/account" });
        } else {
          const rulesPath = resolveRulesPath(err?.policy, err?.errorCode);
          if (rulesPath) setErrorAction({ label: "View Rules", path: rulesPath });
        }
        if (TEXT_MODERATION_BLOCK_CODES.includes(err?.errorCode)) {
          // Keeps the Account Standing *page* in sync — the error banner's
          // own message no longer depends on this refetch (see
          // enforcementPresentation above, sourced directly from this
          // response instead of a second race-prone fetch).
          retryStanding?.();
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return { loading, loadingStage: LOADING_STAGES[loadingStage], error, errorAction, errorCode, enforcementPresentation, submit };
}
